import { signIn, signOut } from "next-auth/react";

import {
  ADMIN_CREDENTIALS_PROVIDER,
  USER_CREDENTIALS_PROVIDER,
} from "@/features/auth/provider-ids";

/**
 * Typed wrappers over NextAuth's client `signIn`/`signOut`. The login UI
 * (Phase 2) calls these instead of passing magic provider ids around, so the
 * handshake details stay in one place.
 */
export interface Credentials {
  username: string;
  password: string;
  rememberMe?: boolean;
}

export interface SignInResult {
  ok: boolean;
  /** NextAuth error code (e.g. "CredentialsSignin", or a backend status). */
  error: string | null;
  /** Redirect target NextAuth resolved (present when `redirect: false`). */
  url: string | null;
}

function normalize(
  res: { ok?: boolean; error?: string | null; url?: string | null } | undefined,
): SignInResult {
  return {
    ok: res?.ok ?? false,
    error: res?.error ?? null,
    url: res?.url ?? null,
  };
}

/** Sign in a regular user against `POST /login`. */
export async function signInUser(
  credentials: Credentials,
  callbackUrl = "/",
): Promise<SignInResult> {
  const res = await signIn(USER_CREDENTIALS_PROVIDER, {
    username: credentials.username,
    password: credentials.password,
    RememberMe: credentials.rememberMe ?? false,
    redirect: false,
    callbackUrl,
  });
  return normalize(res);
}

/** Sign in an admin against `POST /admin/login`. */
export async function signInAdmin(
  credentials: Credentials,
  callbackUrl = "/admin",
): Promise<SignInResult> {
  const res = await signIn(ADMIN_CREDENTIALS_PROVIDER, {
    username: credentials.username,
    password: credentials.password,
    RememberMe: credentials.rememberMe ?? false,
    redirect: false,
    callbackUrl,
  });
  return normalize(res);
}

/**
 * Reduce a post-sign-in redirect target to a *same-origin relative* path.
 *
 * NextAuth resolves `res.url` against `NEXTAUTH_URL`, so the returned URL always
 * carries that configured origin (a fixed IP/host). If we navigated to it
 * absolutely, the browser would jump to that origin — but the session cookie was
 * just set on whatever origin the user actually reached (external IP, hostname,
 * or reverse proxy). The cookie wouldn't travel to the other origin, so the user
 * would land on the homepage logged out. Keeping the target relative pins
 * navigation to the current origin, where the freshly-set cookie lives.
 *
 * Stripping the origin also neutralizes any open-redirect in `res.url`.
 *
 * @param url      the `url` NextAuth returned from `signIn({ redirect: false })`
 * @param fallback where to go when `url` is absent or points back at `/login`
 */
export function resolveSignInTarget(
  url: string | null,
  fallback: string,
): string {
  const candidate = url && !url.includes("/login") ? url : fallback;
  // Already a relative path — use as-is.
  if (candidate.startsWith("/")) return candidate;
  // Absolute URL (carries NEXTAUTH_URL's origin): keep only the path so the
  // browser stays on the origin it's currently on.
  try {
    const parsed = new URL(candidate);
    return `${parsed.pathname}${parsed.search}${parsed.hash}` || "/";
  } catch {
    return fallback;
  }
}

/** Clear the session and return to `/login` (or a given target). */
export function logout(callbackUrl = "/login") {
  return signOut({ callbackUrl });
}
